import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Copy } from "lucide-react";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "https://cdn.morrcart.com";
const APIBASE = process.env.REACT_APP_BACKEND_URL || "https://api.morrcart.com";

export function CodeBlock({ code, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); });
  };
  return (
    <div className="relative bg-[#0A0A0A] text-[#E4E4E7] border border-border">
      <Button size="sm" variant="secondary" onClick={copy} data-testid={testid ? `${testid}-copy` : "snippet-copy"}
        className="absolute top-2 right-2 rounded-none h-7 gap-1 text-xs z-10">
        {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
      </Button>
      <pre className="p-4 pt-10 text-[12px] leading-relaxed font-mono overflow-x-auto no-scrollbar whitespace-pre" data-testid={testid}><code>{code}</code></pre>
    </div>
  );
}

export function InstallSnippet({ slug }) {
  const scriptTag = `<!-- Paste once in your site's <head> or "Custom Code / Header" area. -->
<script id="morrcart-embed" async
  src="${ORIGIN}/embed.js"
  data-tenant="${slug}"></script>`;

  const catalog = `<!-- Show your MorrCart products anywhere. Content comes from MorrCart,
     styling is 100% yours. Group by category is optional. -->
<div data-morrcart-products data-group-by="category"></div>

<!-- OPTIONAL: full control — provide your own template.
     Tokens: {{name}} {{description}} {{price}} {{image}} {{category}} {{cta}} {{id}} -->
<div data-morrcart-products>
  <template data-morrcart-template>
    <article class="your-card">
      <img src="{{image}}" alt="{{name}}" />
      <h3>{{name}}</h3>
      <p>{{description}}</p>
      <strong>{{price}}</strong>
      <button data-morrcart-add data-product-id="{{id}}">{{cta}}</button>
    </article>
  </template>
</div>`;

  const buttons = `<!-- Or wire your own existing buttons (your words, your style) -->

<!-- Open the cart / customer portal -->
<button data-morrcart-open>View Cart</button>
<a href="#" data-morrcart-account>My Account / Login</a>`;

  const events = `<!-- Show your event packages anywhere on your site.
     Cards come from your Events > Packages; clicking one opens the booking wizard. -->
<div data-morrcart-events></div>

<!-- Or wire your own "Book an event" button/link (your words, your style) -->
<button data-morrcart-book>Book an event</button>

<!-- POPUP PACKAGE PICKER: floats a branded gallery of all your packages
     over your own site — no card container needed. Customers browse, tap a
     package to see photos & details, then continue into the booking wizard. -->
<button data-morrcart-book-modal>See event packages</button>

<!-- Deep link straight to the booking wizard from a marketing link/CTA:
     https://yoursite.com/?mc_event=open        (opens the wizard)
     https://yoursite.com/#morrcart-book         (also works)
     https://yoursite.com/?mc_event=packages     (opens the package picker)
     https://yoursite.com/#morrcart-packages      (also works) -->`;

  const hosted = `<!-- No website? Link customers straight to your hosted events page. -->
${ORIGIN}/s/${slug}/book

<!-- Embed that page in an iframe (great for builders that block scripts) -->
<iframe src="${ORIGIN}/s/${slug}/book?embed=1"
  style="width:100%;height:720px;border:0" title="Book an event"></iframe>`;

  const location = `<!-- Your address, hours, phone/email + live map — all managed in MorrCart. -->
<div data-morrcart-location></div>

<!-- Pick a layout per placement (optional):
     vertical (default) · horizontal · stacked · list -->
<div data-morrcart-location data-morrcart-location-layout="horizontal"></div>

<!-- Compact footer version (no map): -->
<div data-morrcart-location data-morrcart-location-layout="list"></div>`;

  const popups = `<!-- Month calendar of your upcoming pop-ups & truck stops. -->
<div data-morrcart-popup-calendar></div>`;

  const popupsHosted = `<!-- Or link/iframe the hosted calendar page: -->
${ORIGIN}/s/${slug}/popups

<iframe src="${ORIGIN}/s/${slug}/popups?embed=1"
  style="width:100%;height:760px;border:0" title="Where to find us"></iframe>`;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h3 className="font-display font-bold text-lg">1 · Add the snippet</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-3">Paste this once into the site's <strong>Head / Custom Code</strong> area (just before <code className="font-mono text-xs">&lt;/body&gt;</code> also works on plain HTML sites). It loads the cart and customer portal — no design changes.</p>
        <CodeBlock code={scriptTag} testid={`install-script-${slug}`} />
        <div className="mt-2 border-l-2 border-[#B7791F] bg-[#B7791F]/5 px-3 py-2 text-xs text-muted-foreground">
          <strong className="text-foreground">Using a website builder (GoHighLevel, Vibe, Wix, Squarespace)?</strong> Add it under the site's <em>Head / Custom Code / Tracking</em> settings, not inside a page block — builders don't run scripts placed in page content. If your site sets a Content-Security-Policy, allow <code className="font-mono">{ORIGIN}</code>.
        </div>
      </div>
      <div>
        <h3 className="font-display font-bold text-lg">2 · Show your products</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-3">Drop in a container and MorrCart renders your products there. Names, descriptions and prices come from your <strong>Products</strong> tab; you style them with your own CSS (default classes: <code className="font-mono text-xs">.mc-product</code>, <code className="font-mono text-xs">.mc-product-name</code>, <code className="font-mono text-xs">.mc-price</code>, <code className="font-mono text-xs">.mc-add-btn</code>).</p>
        <CodeBlock code={catalog} testid={`install-catalog-${slug}`} />
      </div>
      <div>
        <h3 className="font-display font-bold text-lg">3 · Cart & portal buttons</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-3">Add these attributes to any element on the site — style and wording are entirely yours.</p>
        <CodeBlock code={buttons} testid={`install-buttons-${slug}`} />
      </div>
      <div>
        <h3 className="font-display font-bold text-lg">4 · Host events &amp; bookings</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-3">Show your event packages as cards anywhere, or wire your own <strong>Book an event</strong> button. Prefer a popup? Add <code className="font-mono text-xs">data-morrcart-book-modal</code> to any button to float a branded <strong>package picker</strong> over your own site. Clicking a card or button opens the branded booking wizard. Requires the snippet in step 1 and <strong>Events</strong> enabled.</p>
        <CodeBlock code={events} testid={`install-events-${slug}`} />
      </div>
      <div>
        <h3 className="font-display font-bold text-lg">5 · Hosted events page (no code)</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-3">Every store gets a ready-made, branded events page. Link to it from anywhere (menus, Instagram bio, GoHighLevel), or drop it into an iframe.</p>
        <CodeBlock code={hosted} testid={`install-hosted-${slug}`} />
      </div>
      <div>
        <h3 className="font-display font-bold text-lg">6 · Location, hours &amp; contact</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-3">Drop this where you show your address/hours (footer, contact page). It pulls your address, hours, phone/email and a live map straight from MorrCart — update them in <strong>Settings → Location &amp; contact widget</strong> and your site updates itself. Add <code className="font-mono text-xs">data-morrcart-location-layout</code> to pick a layout per placement.</p>
        <CodeBlock code={location} testid={`install-location-${slug}`} />
      </div>
      <div>
        <h3 className="font-display font-bold text-lg">7 · Pop-up event calendar</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-3">A month calendar of your upcoming pop-ups &amp; truck stops — customers can tap a date for details, get <strong>Directions</strong>, and <strong>Add to calendar</strong>. Schedule one-off or repeating stops in <strong>Locations</strong>. Embed it on your site, or share the hosted page.</p>
        <CodeBlock code={popups} testid={`install-popups-${slug}`} />
        <p className="text-sm text-muted-foreground mt-3 mb-2">Hosted page (great for a "Find us" link in your menu or social bio):</p>
        <CodeBlock code={popupsHosted} testid={`install-popups-hosted-${slug}`} />
      </div>
      <div className="border border-border p-4 bg-secondary/50 text-sm">
        <p className="font-medium">Prefer to code it yourself?</p>
        <p className="text-muted-foreground mt-1 font-mono text-xs">MorrCart.getProducts() · MorrCart.add(&#123;id&#125;) · MorrCart.open() · MorrCart.openAccount() · MorrCart.book() · MorrCart.openPackages() · MorrCart.renderCatalogs()</p>
      </div>
    </div>
  );
}
